import { useGetIdentityOrganizations } from '@/hooks/queries/identity';
import { useCanAccessAnalyticsDashboard } from '@/hooks/queries/permissions';
import { useEscapeClosableDropdown } from '@/hooks/useEscapeClosableDropdown';
import { useMenuScrollShadow } from '@/hooks/useMenuScrollShadow';
import { useSsoLogin } from '@/hooks/useSsoLogin';
import { useAuthStore } from '@/store/use-auth-store';
import { useUserStore } from '@/store/use-user-store';
import { getSidebarActiveGradient } from '@/utils/colors';
import { getTenantDisplayName, resolveTenantDisplayName } from '@/utils/tenant-display';
import { reloadIntoTenant } from '@/utils/switch-tenant';
import {
  getLiftHubPath,
  isLiftHubActive,
  isSoulMapConsumerSession,
} from '@/utils/soulmap-auth';
import { useAuth0 } from '@auth0/auth0-react';
import { Icon } from '@iconify/react';
import {
  Button,
  cn,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  ScrollShadow,
  Skeleton,
  Tooltip,
} from '@nextui-org/react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Link, useLocation, useParams } from 'react-router-dom';
import logo from '../../assets/logo.png';
import useGetPractices from '../../hooks/queries/practices/useGetPractices';
import { StandardResponse } from '../../types/agent';

interface IdentityOrganization {
  id: string;
  name: string;
  display_name: string;
}

interface MobileSideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PracticeData {
  id: number;
  name: string;
  description: string;
  created_by: Record<string, string>;
  created_at: string;
}

const MobileSideMenu = ({ isOpen, onClose }: MobileSideMenuProps) => {
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const userId = useUserStore((state) => state.user?.id);
  const {
    authMethod,
    tenants,
    setShowTenantSelector,
  } = useAuthStore();
  const { getIdTokenClaims } = useAuth0();
  const { data: organizations } = useGetIdentityOrganizations(
    authMethod === 'sso'
  );
  const { loginWithSso } = useSsoLogin();
  const tenantDropdown = useEscapeClosableDropdown();
  const tenantMenuScroll = useMenuScrollShadow();

  const {
    data: practicesData,
    isLoading,
    isError,
  } = useGetPractices({ has_posts: true, user_id: userId }, !!userId);

  const recentPractices =
    (practicesData as StandardResponse<PracticeData>)?.results || [];

  const soulMapConsumer = isSoulMapConsumerSession();
  const { hasPermission: canAccessDashboard, isPending: isNavPending } =
    useCanAccessAnalyticsDashboard();

  const navItems = [
    {
      path: getLiftHubPath(tenantSlug || ''),
      label: 'LiFT Hub',
      icon: 'lucide:home',
      activeIcon: 'lucide:home',
    },
    ...(!soulMapConsumer
      ? [
          {
            path: `/${tenantSlug}/discover`,
            label: 'Discover',
            icon: 'lucide:compass',
            activeIcon: 'lucide:compass',
          },
          {
            path: `/${tenantSlug}/library`,
            label: 'Library',
            icon: 'lucide:book-open',
            activeIcon: 'lucide:book-open',
          },
        ]
      : []),
    // While permissions load, skeletons stand in for every item that may show.
    ...(!soulMapConsumer && (isNavPending || canAccessDashboard)
      ? [
          {
            path: `/${tenantSlug}/dashboard`,
            label: 'Dashboard',
            icon: 'lucide:bar-chart-3',
            activeIcon: 'lucide:bar-chart-3',
          },
        ]
      : []),
  ];

  const isActiveRoute = (path: string) =>
    isLiftHubActive(location.pathname, path);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  const formatChatTitle = (name: string, createdAt: string) => {
    const date = new Date(createdAt);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day} - ${name}`;
  };

  const handleNavClick = () => {
    onClose();
  };

  const currentDisplay = resolveTenantDisplayName({
    tenantSlug,
    authMethod,
    tenants,
    organizations: organizations as IdentityOrganization[] | undefined,
  });

  const handleTenantSwitch = async (key: string) => {
    if (key === tenantSlug) return;

    if (authMethod === 'sso') {
      // SSO Flow
      const newOrg = (organizations as IdentityOrganization[])?.find(
        (o: IdentityOrganization) => o.name === key
      );
      if (!newOrg) return;

      const idToken = (await getIdTokenClaims())?.__raw;
      if (!idToken) return;

      await loginWithSso({
        authorizationParams: {
          organization: newOrg.id,
          prompt: 'none',
          id_token_hint: idToken,
        },
      });
      window.location.reload();
    } else {
      // Username/Password Flow
      const newTenant = tenants?.find((t) => t.slug === key);
      if (!newTenant) return;

      setShowTenantSelector(false);
      reloadIntoTenant(newTenant.slug);
    }
  };

  // Use portal to render at document.body level, escaping header's stacking context
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - highest z-index to cover everything */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/30 z-[9999] md:hidden"
          />

          {/* Drawer - even higher z-index */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed top-0 left-0 bottom-0 w-[280px] bg-white z-[10000] flex flex-col shadow-2xl md:hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-white">
              <div className="flex items-center gap-2 overflow-hidden">
                <img
                  src={logo}
                  alt="LiFT Logo"
                  className="h-6 w-6 flex-shrink-0"
                />
                {((authMethod === 'sso' &&
                  organizations &&
                  organizations.length > 1) ||
                  (authMethod !== 'sso' && tenants && tenants.length > 1)) && (
                  <Dropdown {...tenantDropdown.dropdownProps}>
                    <Tooltip content={currentDisplay} placement="bottom">
                      <div className="max-w-full">
                        <DropdownTrigger>
                          <Button
                            {...tenantDropdown.triggerProps}
                            variant="light"
                            className="min-w-0 px-2 text-sm font-medium capitalize"
                            endContent={
                              <Icon
                                icon="lucide:chevron-down"
                                className="text-default-500 flex-shrink-0"
                                width={16}
                              />
                            }
                          >
                            <span className="truncate max-w-[140px]">
                              {currentDisplay}
                            </span>
                          </Button>
                        </DropdownTrigger>
                      </div>
                    </Tooltip>
                    <DropdownMenu
                      aria-label="Tenants"
                      ref={tenantMenuScroll.ref}
                      classNames={{ base: tenantMenuScroll.className }}
                      onAction={(key) => handleTenantSwitch(key as string)}
                    >
                      {authMethod === 'sso'
                        ? (organizations || []).map((org: any) => (
                            <DropdownItem key={org.name}>
                              {getTenantDisplayName(org.display_name)}
                            </DropdownItem>
                          ))
                        : (tenants || []).map((tenant) => (
                            <DropdownItem key={tenant.slug}>
                              {getTenantDisplayName(tenant.name)}
                            </DropdownItem>
                          ))}
                    </DropdownMenu>
                  </Dropdown>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <Icon
                  icon="solar:alt-arrow-left-outline"
                  className="w-5 h-5 text-gray-500"
                />
              </button>
            </div>

            <Divider />
            {/* Navigation Items */}
            <nav className="px-3 py-4 space-y-1">
              {navItems.map((item) => {
                if (isNavPending) {
                  return (
                    <Skeleton
                      key={item.path}
                      className="h-12 w-full rounded-xl"
                    />
                  );
                }

                const active = isActiveRoute(item.path);
                const iconName = active ? item.activeIcon : item.icon;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200',
                      active
                        ? 'text-white shadow-md'
                        : 'text-gray-700 hover:bg-white'
                    )}
                    style={
                      active
                        ? { background: getSidebarActiveGradient(0.85) }
                        : undefined
                    }
                  >
                    <Icon
                      icon={iconName}
                      className={cn(
                        'w-5 h-5 flex-shrink-0',
                        active ? 'text-white' : 'text-gray-500'
                      )}
                    />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            <Divider className="my-2" />

            {/* Recent Chats Section */}
            <div className="flex-1 overflow-hidden px-3 bg-white">
              <div className="flex items-center gap-2 px-4 py-2 text-xs font-normal text-gray-400 uppercase tracking-wider">
                <Icon icon="lucide-message-square" className="w-4 h-4" />
                <span>Recent Chats</span>
              </div>

              <ScrollShadow className="h-full max-h-[calc(100vh-280px)] pb-4">
                {isLoading ? (
                  <div className="space-y-2 px-2">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <Skeleton
                        key={index}
                        className="h-14 w-full rounded-lg"
                      />
                    ))}
                  </div>
                ) : isError || recentPractices.length === 0 ? (
                  <div className="flex items-center justify-center h-20 text-sm text-gray-400">
                    {isError ? 'Failed to load' : 'No recent chats'}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {recentPractices
                      .slice(0, 10)
                      .map((practice: PracticeData) => (
                        <Link
                          key={practice.id}
                          to={`/${tenantSlug}/practices/${practice.id}/run/?action=${encodeURIComponent(practice.name)}&id=${practice.id}`}
                          onClick={handleNavClick}
                          className="block px-4 py-3 rounded-lg hover:bg-white transition-colors"
                        >
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {formatChatTitle(
                              practice.name,
                              practice.created_at
                            )}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {formatRelativeTime(practice.created_at)}
                          </p>
                        </Link>
                      ))}
                  </div>
                )}
              </ScrollShadow>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default MobileSideMenu;
